import React from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { useTheme } from '../../context/ThemeContext';
import { PUBLIC_IMAGES } from '../../constants/publicAssets';
import { getBrandCardSurface } from '../../lib/brandSurface';
import { cn } from '../../lib/utils';

/**
 * Branded KPI card: soft tenant-tint + faint logo watermark.
 * Featured (e.g. Total Revenue) uses solid brand with light type for hierarchy.
 */
export function StatCard({
  title,
  value,
  change,
  trend = 'up',
  context,
  featured = false,
  className = '',
}) {
  const { primaryColor, logoUrl } = useTheme();
  const surface = getBrandCardSurface(primaryColor, { featured });
  // Prefer icon-only VM mark for platform watermark (no “VENUE MANAGER” wordmark).
  // Tenant-uploaded logos still personalize when present.
  const usingPlatformMark = !logoUrl;
  const watermarkSrc = logoUrl || PUBLIC_IMAGES.logoMark;
  const positive = trend === 'up';
  const neutral = trend === 'neutral';
  const badgeVariant = neutral ? 'default' : positive ? 'success' : 'danger';
  const isSolid = surface.mode === 'solid';

  return (
    <article
      className={cn(
        'relative min-w-0 overflow-hidden rounded-xl border',
        className
      )}
      style={{
        background: surface.background,
        borderColor: surface.borderColor,
        boxShadow: surface.shadow,
      }}
    >
      {/*
        Soft cards: faint brand-blue mark (not black) so it stays atmospheric.
        Solid cards: soft white mark. Platform PNG is white-on-black → luminance mask.
      */}
      {usingPlatformMark ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-3 -right-3 h-[4.75rem] w-[4.75rem]"
          style={{
            opacity: isSolid ? 0.16 : 0.12,
            backgroundColor: isSolid ? '#FFFFFF' : primaryColor,
            WebkitMaskImage: `url(${watermarkSrc})`,
            maskImage: `url(${watermarkSrc})`,
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            WebkitMaskMode: 'luminance',
            maskMode: 'luminance',
          }}
        />
      ) : (
        <img
          src={watermarkSrc}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-3 -right-3 h-[4.75rem] w-[4.75rem] select-none object-contain"
          style={{
            opacity: isSolid ? 0.14 : 0.1,
            filter: isSolid ? 'brightness(0) invert(1)' : 'none',
          }}
        />
      )}

      {/* Quiet gold brand cue on soft cards; on solid, a soft top sheen */}
      {!isSolid ? (
        <span
          className="pointer-events-none absolute left-4 top-0 h-0.5 w-8 rounded-full bg-[#C9A227]/80"
          aria-hidden="true"
        />
      ) : (
        <span
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/25"
          aria-hidden="true"
        />
      )}

      <div className="relative z-[1] flex min-h-[7.25rem] min-w-0 items-start justify-between gap-2 px-4 py-5">
        <div className="min-w-0 pr-1">
          <p
            className="font-display text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{ color: surface.titleColor }}
          >
            {title}
          </p>
          {context ? (
            <p className="mt-1 text-[10px] leading-snug" style={{ color: surface.contextColor }}>
              {context}
            </p>
          ) : null}
          <p
            className={cn(
              'mt-3 font-semibold tabular-nums tracking-tight',
              featured ? 'text-[1.5rem] leading-none' : 'text-[1.35rem] leading-none'
            )}
            style={{ color: surface.valueColor }}
          >
            {value}
          </p>
        </div>

        <Badge
          variant={badgeVariant}
          className={cn(
            'shrink-0 gap-1 rounded-full text-[9px]',
            isSolid &&
              (neutral
                ? 'border border-white/25 bg-white/15 text-white'
                : positive
                  ? 'border border-emerald-200/40 bg-emerald-400/20 text-emerald-50'
                  : 'border border-rose-200/40 bg-rose-400/20 text-rose-50')
          )}
        >
          {!neutral && (positive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />)}
          {change}
        </Badge>
      </div>
    </article>
  );
}
